import { useTranslation } from "react-i18next";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";

type ProviderKind = "coach" | "fractional" | "mentor";

const COPY: Record<ProviderKind, { en: { title: string; sub: string; cta: string }; fr: { title: string; sub: string; cta: string } }> = {
  coach: {
    en: {
      title: "Are you an experienced coach?",
      sub: "Join the Bootpreneurs marketplace. Set your rate, your hours, and we bring qualified founders to you. Applications reviewed within 7 days.",
      cta: "Apply to coach",
    },
    fr: {
      title: "Vous êtes coach expérimenté(e) ?",
      sub: "Rejoignez la marketplace Bootpreneurs. Vous fixez votre tarif et vos disponibilités — nous vous présentons des fondateurs qualifiés. Candidatures examinées sous 7 jours.",
      cta: "Candidater comme coach",
    },
  },
  fractional: {
    en: {
      title: "C-level operator looking for fractional roles?",
      sub: "Join our bench of fractional CFOs, CTOs, CMOs and more. Choose your engagements 1–5 days a week. We handle matchmaking, contracts and escrowed payments.",
      cta: "Apply as Fractional Executive",
    },
    fr: {
      title: "Vous êtes un dirigeant expérimenté à temps partagé ?",
      sub: "Rejoignez notre vivier de CFO, CTO, CMO fractional… Choisissez vos missions 1 à 5 jours par semaine. Nous gérons la mise en relation, les contrats et le paiement en escrow.",
      cta: "Candidater comme Fractional Executive",
    },
  },
  mentor: {
    en: {
      title: "Want to mentor the next generation of founders?",
      sub: "Run 1:1 sessions on your own schedule. Pick the topics you love. We send qualified founders your way and handle billing.",
      cta: "Apply as Mentor",
    },
    fr: {
      title: "Envie de mentorer la nouvelle génération de fondateurs ?",
      sub: "Animez des sessions 1:1 selon vos disponibilités. Choisissez vos sujets de prédilection. Nous vous envoyons des fondateurs qualifiés et gérons la facturation.",
      cta: "Candidater comme Mentor",
    },
  },
};

export function BecomeProviderCTA({ kind }: { kind: ProviderKind }) {
  const { i18n } = useTranslation();
  const lang: "en" | "fr" = (i18n.language || "en").startsWith("fr") ? "fr" : "en";
  const c = COPY[kind][lang];

  return (
    <section
      className="my-12 rounded-2xl border border-primary/20 p-8 md:p-10"
      style={{
        background:
          "linear-gradient(135deg, hsl(var(--primary) / 0.08), hsl(var(--accent) / 0.10))",
      }}
    >
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="max-w-2xl">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-background/60 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary">
            <Sparkles className="h-3 w-3" />
            {lang === "fr" ? "Rejoindre le réseau" : "Join the network"}
          </div>
          <h3 className="font-display text-2xl font-bold leading-tight md:text-3xl">{c.title}</h3>
          <p className="mt-3 text-sm text-muted-foreground md:text-base">{c.sub}</p>
          <ol className="mt-4 grid gap-2 text-xs text-muted-foreground md:grid-cols-3">
            <li>
              <span className="mr-1 font-bold text-primary">1.</span>
              {lang === "fr" ? "Postuler en 7 minutes" : "Apply in 7 minutes"}
            </li>
            <li>
              <span className="mr-1 font-bold text-primary">2.</span>
              {lang === "fr" ? "Appel de cadrage sous 7 jours" : "Vetting call within 7 days"}
            </li>
            <li>
              <span className="mr-1 font-bold text-primary">3.</span>
              {lang === "fr" ? "Onboarding & premières missions" : "Onboarding & first bookings"}
            </li>
          </ol>
        </div>
        <Button asChild size="lg" className="shrink-0">
          <Link to={`/apply/form?type=${kind}`}>
            {c.cta} <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </section>
  );
}

export default BecomeProviderCTA;